import React, { Component } from 'react'
import * as XLSX from 'xlsx'
import ElementsDataService from '../../api/entitiesDataServices/ElementsDataService.js'

// Normaliza un string: minúsculas, sin acentos, sin espacios extra
function norm(s) {
    return String(s || '')
        .trim()
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
}

// Limpia y convierte una fecha al formato yyyy-MM-dd que espera el backend
function parseDate(val) {
    if (val === null || val === undefined || val === '') return ''
    // Objeto Date
    if (val instanceof Date) return val.toISOString().split('T')[0]
    // Número serial de Excel (0 = 1900-01-00, ignorar)
    if (typeof val === 'number') {
        if (val <= 0 || val > 80000) return ''
        const d = XLSX.SSF.parse_date_code(val)
        if (!d || d.y < 1990) return ''
        return `${d.y}-${String(d.m).padStart(2,'0')}-${String(d.d).padStart(2,'0')}`
    }
    const s = String(val).trim()
    if (!s || s === '0') return ''

    // Buscar patrón dd/MM/yyyy dentro del texto (cubre "firma activa hasta el dia 05/10/2027", "(CADUCADA)", etc.)
    const dmyInText = s.match(/(\d{1,2})\/(\d{1,2})\/(\d{4})/)
    if (dmyInText) {
        const [, d, m, y] = dmyInText
        // Descartar "0/1/1900"
        if (Number(y) < 1990) return ''
        return `${y}-${m.padStart(2,'0')}-${d.padStart(2,'0')}`
    }

    // Formato yyyy/MM/dd  (ej: "2017/10/04")
    const ymdSlash = s.match(/^(\d{4})\/(\d{1,2})\/(\d{1,2})$/)
    if (ymdSlash) return `${ymdSlash[1]}-${ymdSlash[2].padStart(2,'0')}-${ymdSlash[3].padStart(2,'0')}`

    // Formato yyyy-MM-dd dentro del texto (ej: "VENCIDA \n2024-09-21")
    const isoInText = s.match(/(\d{4})-(\d{2})-(\d{2})/)
    if (isoInText) {
        if (Number(isoInText[1]) < 1990) return ''
        return `${isoInText[1]}-${isoInText[2]}-${isoInText[3]}`
    }

    return ''
}

// Valores que deben tratarse como vacío en correo y contraseña
const EMPTY_VALUES = new Set([
    'no', 'sin correo', 'sin contraseña', 'sin contrasena', 'sin registro',
    'no localizada', 'nohay registro', 'sn correo', 'sn contra',
    'sn acceso al correo', '#n/d', '0', '0/1/1900', 'inactivo',
])

function sanitizeText(val) {
    const s = String(val || '').trim()
    if (!s) return ''
    if (EMPTY_VALUES.has(s.toLowerCase())) return ''
    return s
}

// Claves normalizadas → campo del modelo
const COLUMN_MAP = {
    'rfc':                          'rfc',
    'nombre de la sociedad':        'razonSocial',
    'auxiliar contable':            'auxiliarContable',
    'responsable contabilidad':     'responsableContabilidad',
    'tipo de sociedad':             'tipo',
    'tipo sociedad':                'tipo',
    'suspension de actividades':    'situacion',
    'observaciones':                'pendientes',
    'status':                       'statusContabilidad',
    'status contable':              'statusContabilidad',
    'estatus contable':             'statusContabilidad',
    'status legal':                 'statusLegal',
    'estatus legal':                'statusLegal',
    'vencimiento e.firma':          'vigenciaFiel',
    'vencimiento efirma':           'vigenciaFiel',
    'vencimiento':                  'vigenciaFiel',
    'correo electronico':           'correo',
    'buzon tributario':             'buzonTributarioStatus',
    'contrasena':                   'password',
    'contrasena del correo':        'password',
    'proyecto':                     '_proyecto',
}

class SociedadImportComponent extends Component {
    // props: onClose, allClientes, existingRfcs (Set), onSuccess
    constructor(props) {
        super(props)
        this.state = {
            step: 'idle',   // 'idle' | 'preview' | 'loading' | 'done'
            rows: [],
            result: null,
            error: null,
            showPasswords: false,
            rejectedRows: [],       // filas con ERROR detectado en el frontend (antes de enviar)
            expectedCrear: 0,
            expectedActualizar: 0,
        }
        this.fileRef = React.createRef()
        this.handleFile = this.handleFile.bind(this)
        this.handleConfirm = this.handleConfirm.bind(this)
    }

    handleFile(file) {
        if (!file) return
        this.setState({ error: null })
        const reader = new FileReader()
        reader.onload = (e) => {
            try {
                const wb = XLSX.read(e.target.result, { type: 'array', cellDates: true })
                const ws = wb.Sheets[wb.SheetNames[0]]
                const raw = XLSX.utils.sheet_to_json(ws, { defval: '' })

                const { allClientes, existingRfcs } = this.props
                const clienteMap = {}
                allClientes.forEach(c => {
                    clienteMap[norm(c.nombreCliente)] = c
                })

                // Construir mapa: header normalizado → valor real de la fila
                // Así el matching es case-insensitive y sin acentos

                const rows = raw
                    .map((row, i) => {
                        // Normalizar las keys del row para comparación
                        const normRow = {}
                        Object.entries(row).forEach(([k, v]) => {
                            normRow[norm(k)] = v
                        })

                        const s = {}
                        Object.entries(COLUMN_MAP).forEach(([colNorm, field]) => {
                            const val = normRow[colNorm]
                            // No sobreescribir si ya tiene valor (evita que alias vacíos borren el campo)
                            if (val === undefined || val === null) return
                            if (String(val).trim() === '') return
                            if (s[field]) return
                            if (field === 'vigenciaFiel') {
                                s[field] = parseDate(val)
                            } else if (field === 'correo' || field === 'password') {
                                s[field] = sanitizeText(val)
                            } else if (field === 'statusContabilidad' || field === 'statusLegal') {
                                s[field] = String(val).trim().toUpperCase()
                            } else {
                                s[field] = String(val).trim()
                            }
                        })

                        // Proyecto → clientes array
                        // Soporta múltiples proyectos separados por "/" o ","
                        // Para "-": solo divide si TODOS los fragmentos coinciden con un cliente conocido
                        //           (evita partir nombres compuestos como CLN-BANORTE o HERM-CUL)
                        const proyectoStr = String(s._proyecto || '').trim()
                        delete s._proyecto
                        s.clientes = proyectoStr
                            ? proyectoStr
                                .split(/[\/,]/)
                                .map(p => p.trim())
                                .filter(Boolean)
                                .flatMap(part => {
                                    const direct = clienteMap[norm(part)]
                                    if (direct) return [direct]
                                    // Intentar dividir por guión solo si cada fragmento existe
                                    const dashParts = part.split('-').map(p => p.trim()).filter(Boolean)
                                    if (dashParts.length > 1) {
                                        const dashMatches = dashParts.map(p => clienteMap[norm(p)])
                                        if (dashMatches.every(Boolean)) return dashMatches
                                    }
                                    return [] // sin match
                                })
                            : []

                        // Determinar acción
                        const rfc = s.rfc.toUpperCase()
                        let _action, _errorMsg
                        if (!rfc) {
                            _action = 'ERROR'
                            _errorMsg = 'RFC vacío'
                        } else if (!s.razonSocial) {
                            _action = 'ERROR'
                            _errorMsg = 'Nombre de la Sociedad vacío'
                        } else {
                            _action = existingRfcs.has(rfc) ? 'ACTUALIZAR' : 'CREAR'
                        }

                        return { ...s, rfc, _action, _errorMsg, _row: i + 2 }
                    })
                    // Ignorar filas completamente vacías
                    .filter(r => r.rfc || r.razonSocial)

                if (rows.length === 0) {
                    this.setState({ error: 'No se encontraron filas válidas. Verifica que las columnas tengan los nombres correctos.' })
                    return
                }

                this.setState({ rows, step: 'preview' })
            } catch (err) {
                this.setState({ error: 'Error al leer el archivo. Verifica que sea un .xlsx válido.' })
            }
        }
        reader.readAsArrayBuffer(file)
    }

    handleConfirm() {
        const valid    = this.state.rows.filter(r => r._action !== 'ERROR')
        const rejected = this.state.rows.filter(r => r._action === 'ERROR')
        if (!valid.length && !rejected.length) return

        this.setState({ step: 'loading', rejectedRows: rejected })

        const payload = valid.map(({ _action, _row, _errorMsg, ...s }) => ({
            ...s,
            // Enviar solo el id de cada cliente, no el objeto completo
            clientes: (s.clientes || []).map(c => ({ id: c.id })),
        }))

        console.log('[Import] Payload enviado al backend:', JSON.stringify(payload, null, 2))

        ElementsDataService.importSociedades(payload)
            .then(r => {
                console.log('[Import] Respuesta del backend:', JSON.stringify(r.data, null, 2))
                const result = r.data
                // Advertencia: el frontend esperaba X creates pero backend dice otra cosa
                const expectedCrear     = valid.filter(v => !this.props.existingRfcs.has(v.rfc)).length
                const expectedActualizar = valid.filter(v =>  this.props.existingRfcs.has(v.rfc)).length
                if (expectedCrear > 0 && (result.creadas || 0) === 0) {
                    console.warn(`[Import] ⚠️ Frontend esperaba ${expectedCrear} CREAR pero backend reporta creadas=${result.creadas}. Posible problema en el backend.`)
                }
                this.setState({ step: 'done', result, expectedCrear, expectedActualizar })
            })
            .catch(err => {
                const msg = err?.response?.data?.message || err?.response?.data || 'Error al importar. Intente nuevamente.'
                console.error('[Import] Error data:', JSON.stringify(err?.response?.data))
                this.setState({ step: 'preview', error: String(msg) })
            })
    }

    render() {
        const { step, rows, result, error, rejectedRows, expectedCrear, expectedActualizar } = this.state
        const { onClose } = this.props

        const crear      = rows.filter(r => r._action === 'CREAR').length
        const actualizar = rows.filter(r => r._action === 'ACTUALIZAR').length
        const errores    = rows.filter(r => r._action === 'ERROR').length
        const { showPasswords } = this.state

        return (
            <div style={styles.overlay}>
                <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
                <div style={styles.modal}>

                    {/* Header */}
                    <div style={styles.header}>
                        <div>
                            <h2 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 700, color: '#0f172a', fontFamily: "'Inter', sans-serif" }}>
                                Importar Sociedades desde Excel
                            </h2>
                            {step === 'preview' && (
                                <p style={{ margin: '4px 0 0', fontSize: '0.78rem', color: '#64748b', fontFamily: "'Inter', sans-serif" }}>
                                    <span style={{ color: '#16a34a', fontWeight: 600 }}>{crear} por crear</span>
                                    {' · '}
                                    <span style={{ color: '#2563eb', fontWeight: 600 }}>{actualizar} por actualizar</span>
                                    {errores > 0 && <>{' · '}<span style={{ color: '#dc2626', fontWeight: 600 }}>{errores} con error</span></>}
                                </p>
                            )}
                        </div>
                        <button onClick={onClose} style={styles.btnClose}>✕</button>
                    </div>

                    {/* Body */}
                    <div style={{ padding: '24px', overflowY: 'auto', flex: 1 }}>

                        {error && (
                            <div style={styles.errorBanner}>{error}</div>
                        )}

                        {/* Step: idle — file picker */}
                        {step === 'idle' && (
                            <div style={styles.dropZone} onClick={() => this.fileRef.current.click()}>
                                <div style={{ fontSize: '2.5rem', marginBottom: 10 }}>📂</div>
                                <div style={{ fontWeight: 600, color: '#334155', marginBottom: 6, fontFamily: "'Inter', sans-serif" }}>
                                    Seleccionar archivo Excel
                                </div>
                                <div style={{ fontSize: '0.78rem', color: '#94a3b8', fontFamily: "'Inter', sans-serif" }}>
                                    Formatos: .xlsx · .xls
                                </div>
                                <input
                                    ref={this.fileRef}
                                    type="file"
                                    accept=".xlsx,.xls"
                                    style={{ display: 'none' }}
                                    onChange={e => this.handleFile(e.target.files[0])}
                                />
                            </div>
                        )}

                        {/* Step: preview — tabla de confirmación */}
                        {step === 'preview' && (
                            <div style={{ maxHeight: 420, overflowY: 'auto', borderRadius: 10, border: '1px solid #e2e8f0' }}>
                                <table style={{ width: '100%', tableLayout: 'fixed', borderCollapse: 'collapse', fontSize: '0.73rem', fontFamily: "'Inter', sans-serif" }}>
                                    <colgroup>
                                        <col style={{ width: '7%' }} />   {/* Estado */}
                                        <col style={{ width: '3%' }} />   {/* Fila */}
                                        <col style={{ width: '8%' }} />   {/* RFC */}
                                        <col style={{ width: '13%' }} />  {/* Razón Social */}
                                        <col style={{ width: '6%' }} />   {/* Proyecto */}
                                        <col style={{ width: '7%' }} />   {/* Resp. Cont. */}
                                        <col style={{ width: '7%' }} />   {/* Aux. Cont. */}
                                        <col style={{ width: '6%' }} />   {/* Est. Cont. */}
                                        <col style={{ width: '6%' }} />   {/* Est. Legal */}
                                        <col style={{ width: '7%' }} />   {/* Venc. Firma */}
                                        <col style={{ width: '16%' }} />  {/* Correo */}
                                        <col style={{ width: '8%' }} />   {/* Contraseña */}
                                    </colgroup>
                                    <thead>
                                        <tr>
                                            {['Estado','#','RFC','Razón Social','Proyecto','Resp.Cont.','Aux.Cont.','Est.Cont.','Est.Legal','Venc.Firma','Correo'].map(h => (
                                                <th key={h} style={styles.th}>{h}</th>
                                            ))}
                                            <th style={styles.th}>
                                                <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                                    Contraseña
                                                    <button
                                                        onClick={() => this.setState({ showPasswords: !showPasswords })}
                                                        title={showPasswords ? 'Ocultar contraseñas' : 'Mostrar contraseñas'}
                                                        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, lineHeight: 1, color: showPasswords ? '#3b82f6' : '#94a3b8', display: 'flex' }}
                                                    >
                                                        {showPasswords ? (
                                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
                                                            </svg>
                                                        ) : (
                                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                                <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/>
                                                            </svg>
                                                        )}
                                                    </button>
                                                </span>
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {rows.map((r, i) => (
                                            <tr key={i} style={{ background: i % 2 === 0 ? '#fff' : '#f8fafc', borderBottom: '1px solid #f1f5f9' }}>
                                                <td style={{ ...styles.td, whiteSpace: 'nowrap' }}>
                                                    <span style={badgeFor(r._action)}>{r._action}</span>
                                                    {r._errorMsg && (
                                                        <div style={{ fontSize: '0.65rem', color: '#ef4444', marginTop: 2 }}>{r._errorMsg}</div>
                                                    )}
                                                </td>
                                                <td style={{ ...styles.td, color: '#94a3b8', textAlign: 'center' }}>{r._row}</td>
                                                <td style={{ ...styles.td, fontFamily: 'monospace', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.rfc || <Dash />}</td>
                                                <td style={{ ...styles.td, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={r.razonSocial}>{r.razonSocial || <Dash />}</td>
                                                <td style={{ ...styles.td, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                    {r.clientes?.length ? r.clientes.map(c => c.nombreCliente).join(', ') : <Dash />}
                                                </td>
                                                <td style={{ ...styles.td, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.responsableContabilidad || <Dash />}</td>
                                                <td style={{ ...styles.td, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.auxiliarContable || <Dash />}</td>
                                                <td style={{ ...styles.td, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.statusContabilidad || <Dash />}</td>
                                                <td style={{ ...styles.td, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.statusLegal || <Dash />}</td>
                                                <td style={{ ...styles.td, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.vigenciaFiel || <Dash />}</td>
                                                <td style={{ ...styles.td, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={r.correo}>{r.correo || <Dash />}</td>
                                                <td style={{ ...styles.td, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                    {r.password
                                                        ? showPasswords
                                                            ? <span style={{ fontFamily: 'monospace', fontSize: '0.75rem', color: '#334155' }}>{r.password}</span>
                                                            : <span style={{ letterSpacing: 3, color: '#94a3b8', fontSize: '0.7rem' }}>••••••</span>
                                                        : <Dash />}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        {/* Step: loading */}
                        {step === 'loading' && (
                            <div style={{ textAlign: 'center', padding: '56px 0' }}>
                                <div style={{ width: 36, height: 36, border: '3px solid #e2e8f0', borderTopColor: '#3b82f6', borderRadius: '50%', animation: 'spin 0.7s linear infinite', margin: '0 auto 16px' }} />
                                <p style={{ color: '#64748b', fontSize: '0.85rem', fontFamily: "'Inter', sans-serif" }}>Importando sociedades...</p>
                            </div>
                        )}

                        {/* Step: done */}
                        {step === 'done' && result && (() => {
                            const beErrors   = result.errores || []
                            const totalFail  = beErrors.length + rejectedRows.length
                            const allOk      = totalFail === 0
                            // Detectar discrepancia: frontend esperaba CREARs pero backend dice 0 creadas
                            const mismatch = expectedCrear > 0 && (result.creadas || 0) === 0
                            return (
                                <div style={{ fontFamily: "'Inter', sans-serif" }}>

                                    {/* Título */}
                                    <div style={{ textAlign: 'center', marginBottom: 24 }}>
                                        <div style={{ fontSize: '2rem', marginBottom: 8 }}>{allOk && !mismatch ? '✅' : '⚠️'}</div>
                                        <h3 style={{ margin: 0, color: '#0f172a', fontSize: '1.05rem' }}>
                                            {allOk && !mismatch ? 'Importación completada sin errores' : 'Importación completada con observaciones'}
                                        </h3>
                                    </div>

                                    {/* Advertencia de discrepancia */}
                                    {mismatch && (
                                        <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: '0.8rem', color: '#92400e', fontFamily: "'Inter', sans-serif" }}>
                                            <strong>⚠️ Discrepancia detectada:</strong> el frontend identificó <strong>{expectedCrear} sociedad(es) nuevas</strong> (CREAR), pero el backend reporta <strong>{result.creadas || 0} creadas</strong>.
                                            Revisa la consola del navegador (F12 → Console) para ver el payload enviado y la respuesta exacta del servidor.
                                            Es posible que el backend las esté tratando como actualizaciones de registros existentes con el mismo RFC.
                                        </div>
                                    )}

                                    {/* Resumen de conteos */}
                                    <div style={{ display: 'flex', justifyContent: 'center', gap: 20, marginBottom: 28, flexWrap: 'wrap' }}>
                                        <StatBox label="Creadas"         value={result.creadas}       color="#16a34a" />
                                        <StatBox label="Actualizadas"    value={result.actualizadas}  color="#2563eb" />
                                        <StatBox label="Error (backend)" value={beErrors.length}      color="#dc2626" />
                                        <StatBox label="Rechazadas"      value={rejectedRows.length}  color="#f59e0b" />
                                    </div>

                                    {/* Detalle: rechazadas por el frontend (datos inválidos) */}
                                    {rejectedRows.length > 0 && (
                                        <div style={{ marginBottom: 16 }}>
                                            <div style={resultSectionHeader('#fef3c7', '#92400e', '#fde68a')}>
                                                ⚠️ Rechazadas antes de enviar ({rejectedRows.length}) — datos inválidos en el Excel
                                            </div>
                                            <div style={{ border: '1px solid #fde68a', borderTop: 'none', borderRadius: '0 0 8px 8px', overflow: 'hidden' }}>
                                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.75rem' }}>
                                                    <thead>
                                                        <tr style={{ background: '#fffbeb' }}>
                                                            <th style={styles.th}>#Fila</th>
                                                            <th style={styles.th}>RFC</th>
                                                            <th style={styles.th}>Razón Social</th>
                                                            <th style={styles.th}>Motivo</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {rejectedRows.map((r, i) => (
                                                            <tr key={i} style={{ borderBottom: '1px solid #fef3c7', background: i % 2 === 0 ? '#fff' : '#fffbeb' }}>
                                                                <td style={{ ...styles.td, color: '#94a3b8', textAlign: 'center' }}>{r._row}</td>
                                                                <td style={{ ...styles.td, fontFamily: 'monospace' }}>{r.rfc || '—'}</td>
                                                                <td style={{ ...styles.td }}>{r.razonSocial || '—'}</td>
                                                                <td style={{ ...styles.td, color: '#b45309', fontWeight: 600 }}>{r._errorMsg || 'Error desconocido'}</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    )}

                                    {/* Detalle: errores del backend */}
                                    {beErrors.length > 0 && (
                                        <div style={{ marginBottom: 16 }}>
                                            <div style={resultSectionHeader('#fef2f2', '#991b1b', '#fecaca')}>
                                                ❌ Errores al guardar ({beErrors.length}) — rechazadas por el servidor
                                            </div>
                                            <div style={{ border: '1px solid #fecaca', borderTop: 'none', borderRadius: '0 0 8px 8px', padding: '12px 16px', background: '#fff' }}>
                                                {beErrors.map((e, i) => (
                                                    <div key={i} style={{ fontSize: '0.75rem', color: '#b91c1c', padding: '4px 0', borderBottom: i < beErrors.length - 1 ? '1px solid #fee2e2' : 'none' }}>
                                                        • {e}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* Mensaje si todo OK */}
                                    {allOk && (
                                        <div style={{ textAlign: 'center', color: '#16a34a', fontSize: '0.85rem' }}>
                                            Todas las sociedades fueron procesadas correctamente.
                                        </div>
                                    )}
                                </div>
                            )
                        })()}
                    </div>

                    {/* Footer */}
                    {(step === 'preview' || step === 'done') && (
                        <div style={styles.footer}>
                            {step === 'preview' && (
                                <>
                                    <button
                                        onClick={() => this.setState({ step: 'idle', rows: [], error: null })}
                                        style={styles.btnSecondary}
                                    >
                                        ← Cambiar archivo
                                    </button>
                                    <button
                                        onClick={this.handleConfirm}
                                        disabled={crear + actualizar === 0}
                                        style={{ ...styles.btnPrimary, opacity: crear + actualizar === 0 ? 0.5 : 1, cursor: crear + actualizar === 0 ? 'not-allowed' : 'pointer' }}
                                    >
                                        Confirmar importación ({crear + actualizar})
                                    </button>
                                </>
                            )}
                            {step === 'done' && (
                                <button
                                    onClick={() => { if (this.props.onSuccess) this.props.onSuccess(); onClose() }}
                                    style={styles.btnPrimary}
                                >
                                    Cerrar
                                </button>
                            )}
                        </div>
                    )}
                </div>
            </div>
        )
    }
}

function resultSectionHeader(bg, color, borderColor) {
    return {
        background: bg, color, border: `1px solid ${borderColor}`,
        borderRadius: '8px 8px 0 0', padding: '8px 14px',
        fontSize: '0.78rem', fontWeight: 700, fontFamily: "'Inter', sans-serif",
    }
}

function Dash() {
    return <span style={{ color: '#cbd5e1' }}>—</span>
}

function StatBox({ label, value, color }) {
    return (
        <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '2rem', fontWeight: 700, color, fontFamily: "'Inter', sans-serif" }}>{value}</div>
            <div style={{ fontSize: '0.78rem', color: '#64748b', fontFamily: "'Inter', sans-serif" }}>{label}</div>
        </div>
    )
}

function badgeFor(action) {
    const base = { fontSize: '0.68rem', fontWeight: 700, padding: '2px 8px', borderRadius: 20, border: '1px solid', letterSpacing: '0.5px', fontFamily: "'Inter', sans-serif" }
    if (action === 'CREAR')      return { ...base, background: '#dcfce7', color: '#16a34a', borderColor: '#bbf7d0' }
    if (action === 'ACTUALIZAR') return { ...base, background: '#dbeafe', color: '#2563eb', borderColor: '#bfdbfe' }
    return { ...base, background: '#fee2e2', color: '#dc2626', borderColor: '#fecaca' }
}

const styles = {
    overlay: {
        position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1000, fontFamily: "'Inter', sans-serif",
    },
    modal: {
        background: 'white', borderRadius: 16,
        width: '96%', maxWidth: 1400, maxHeight: '90vh',
        display: 'flex', flexDirection: 'column',
        boxShadow: '0 24px 64px rgba(0,0,0,0.22)',
    },
    header: {
        display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
        padding: '20px 24px', borderBottom: '1px solid #e8edf5',
    },
    footer: {
        display: 'flex', justifyContent: 'flex-end', gap: 10,
        padding: '16px 24px', borderTop: '1px solid #e8edf5',
    },
    btnClose: { background: 'none', border: 'none', fontSize: '1rem', cursor: 'pointer', color: '#94a3b8', padding: 4, lineHeight: 1 },
    btnPrimary: {
        padding: '9px 20px', background: 'linear-gradient(135deg, #2563eb, #3b82f6)',
        border: 'none', borderRadius: 10, color: 'white', fontWeight: 600,
        fontSize: '0.85rem', cursor: 'pointer', fontFamily: "'Inter', sans-serif",
    },
    btnSecondary: {
        padding: '9px 20px', background: 'white', border: '1px solid #e2e8f0',
        borderRadius: 10, color: '#475569', fontWeight: 600, fontSize: '0.85rem',
        cursor: 'pointer', fontFamily: "'Inter', sans-serif",
    },
    dropZone: {
        border: '2px dashed #bfdbfe', borderRadius: 12, padding: '48px 24px',
        textAlign: 'center', cursor: 'pointer', background: '#f8faff',
        transition: 'border-color 0.2s',
    },
    th: {
        padding: '9px 12px', textAlign: 'left', fontSize: '0.68rem', fontWeight: 700,
        color: '#64748b', letterSpacing: '0.8px', textTransform: 'uppercase',
        background: '#f8fafc', borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap',
        position: 'sticky', top: 0,
    },
    td: { padding: '8px 12px', color: '#334155', verticalAlign: 'middle' },
    errorBanner: {
        background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8,
        padding: '10px 14px', marginBottom: 16, fontSize: '0.82rem',
        color: '#dc2626', fontFamily: "'Inter', sans-serif",
    },
}

export default SociedadImportComponent
